import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { FiActivity, FiTrendingUp, FiEye, FiEyeOff } from 'react-icons/fi';
import { getNodeAccent, isNodeShownOnChart, buildNodeVisualTogglePatch } from '../config/nodeVisualConfig';
import { useNodeVisualOverrides } from '../../../../../hooks/useVisualOverrides';
import { Tooltip } from '../../../../../components/ds';

/**
 * MinimalIndicatorNode
 *
 * A sleek, compact representation of an indicator for the modern Strategy Builder.
 * Designed to be stacked vertically with a table-like parameter view.
 */
const MinimalIndicatorNode = ({ id: nodeId, data, selected }) => {
    const { effectiveData, write: writeDisplay } = useNodeVisualOverrides(nodeId, data);

    // Extract key display props
    const label = data.label || data.name || data.indicator || 'Indicator';
    const params = data.params || {};
    const id = nodeId || data.id || ''; // Ensure ID is available
    const accent = getNodeAccent('indicator', data); // cyan (classification)

    //! R1-T3 — ONE toggle helper + the ONE display writer (VisualOverridesContext); never a direct ReactFlow
    //  node write for display keys. The eye state is evaluated against the EFFECTIVE node (R2-T4).
    const toggleChart = useCallback((e) => {
        e.stopPropagation();
        writeDisplay?.(buildNodeVisualTogglePatch('indicator', effectiveData, 'visibility', { defaultType: 'line' }));
    }, [writeDisplay, effectiveData]);

    const isChartVisible = isNodeShownOnChart('indicator', effectiveData);

    const onShowSlope = (e) => {
        e.stopPropagation();
        if (data.onShowSlope) data.onShowSlope(id);
    };
    // Default timeframe to '5m' if missing to ensure consistent look
    const timeframe = data.timeframe || data.period;

    // Neutral DS box; classification colour lives on the stripe + icon (see below).
    const containerStyle = selected
        ? "ring-2 ring-[var(--color-accent)] border-transparent shadow-lg"
        : data.isHighlighted
            ? "ring-2 ring-[var(--color-accent)] border-transparent scale-[1.02] shadow-lg"
            : "border-[var(--divider)] hover:border-[var(--border)]";

    // Helper to format params compactly (Up to 6 items in 3 columns)
    const renderParams = () => {
        let entries = Object.entries(params);
        const isFieldExtractor = data.isFieldExtractor || data.indicator === 'FieldExtractor' || label === 'Field Extractor';

        // Universal filter: allow parameters to be hidden by default (like timeframe which is in badge)
        entries = entries.filter(([key]) => key !== 'timeframe');

        // Special handling for Field Extractor: Show only Source Field
        if (isFieldExtractor) {
            const sourceEntry = entries.find(([k]) => k === 'source');
            const fieldEntry = entries.find(([k]) => k === 'field');

            if (sourceEntry) {
                entries = [sourceEntry];
            } else if (fieldEntry) {
                 entries = [fieldEntry];
            } else {
                entries = [];
            }
        }

        entries = entries.slice(0, 6);
        if (entries.length === 0) return null;

        return (
            <div className="grid grid-cols-3 gap-x-2 gap-y-2 mt-3 pt-2 border-t border-[var(--divider)]">
                {entries.map(([key, val]) => (
                    <div key={key} className="flex flex-col">
                        <span className="text-[7px] font-bold text-fg-3 uppercase tracking-wider truncate">
                            {key === 'source' ? 'SOURCE FIELD' : key.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] font-mono font-medium text-fg-1 truncate">
                            {val}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className={`relative w-[250px] rounded-lg border bg-bg-2 transition-all duration-200 group ${containerStyle}`}>
            {/* Classification stripe (indicator = cyan) — clipped to the rounded corners */}
            <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none" aria-hidden="true">
                <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
            </div>

            {/* Input Handle (Left) */}
            <Handle
                type="target"
                id="input_0"
                position={Position.Left}
                className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] transition-transform hover:scale-125 shadow-sm"
                style={{ background: accent }}
            />

            <div className="p-3 pl-4">
                {/* Header Row: Label + Timeframe Badge */}
                <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 overflow-hidden flex-1">
                        <FiActivity className="shrink-0" size={14} style={{ color: accent }} />
                        <span className="text-sm font-bold text-fg-1 truncate uppercase tracking-tight">
                            {label}
                        </span>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                        {/* Visibility Toggle */}
                        <Tooltip content={isChartVisible ? "Hide from Chart" : "Show on Chart"} placement="top">
                        <button
                            onClick={toggleChart}
                            aria-label={isChartVisible ? "Hide from Chart" : "Show on Chart"}
                            className={`p-1 rounded transition-all duration-200 ${
                                isChartVisible
                                    ? "text-[var(--color-accent)] bg-bg-3"
                                    : "text-fg-3 hover:text-fg-2 hover:bg-bg-3"
                            }`}
                        >
                            {isChartVisible ? <FiEye size={12} /> : <FiEyeOff size={12} />}
                        </button>
                        </Tooltip>

                        {/* Quick Action: Slope Icon (Subtle Toggle) */}
                        {data.indicator !== 'dee_trend_slope' && (
                            <Tooltip content={data.isSlopeAttached ? "Slope Already Attached" : "Show Trend Slope"} placement="top">
                            <button
                                onClick={onShowSlope}
                                aria-label={data.isSlopeAttached ? "Slope Already Attached" : "Show Trend Slope"}
                                className="p-1 rounded transition-all duration-300 group/slope hover:bg-bg-3"
                                style={data.isSlopeAttached ? { background: accent, color: '#fff' } : { color: accent }}
                            >
                                <FiTrendingUp
                                    size={12}
                                    className={`${data.isSlopeAttached ? "scale-110" : "group-hover/slope:scale-110"} transition-transform`}
                                />
                            </button>
                            </Tooltip>
                        )}

                        {/* Timeframe Badge (Visible Only if Set) */}
                        {timeframe && (
                            <div className="flex items-center">
                                <span className="text-[10px] font-bold text-fg-2 bg-bg-3 px-2 py-0.5 rounded border border-[var(--divider)]">
                                    {typeof timeframe === 'number' ? `P: ${timeframe}` : timeframe}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Parameter Grid (Table-like) */}
                {renderParams()}

            </div>

            {/* Output Handle (Right) — accent-tinted */}
            <Handle
                type="source"
                position={Position.Right}
                className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] transition-transform hover:scale-125 shadow-sm"
                style={{ background: accent }}
            />
        </div>
    );
};

export default memo(MinimalIndicatorNode);
